import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Quote } from '../../types/quote.types';
import { Lookup } from '../../types/lookup.types';
import * as lookupApi from '../../api/lookups';

interface QuoteRejectionModalProps {
  open: boolean;
  onClose: () => void;
  quote: Quote | null;
  onReject: (quoteId: string, rejectionReason: string) => Promise<void>;
  loading?: boolean;
}

const QuoteRejectionModal: React.FC<QuoteRejectionModalProps> = ({
  open,
  onClose,
  quote,
  onReject,
  loading = false,
}) => {
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [rejectionReasons, setRejectionReasons] = useState<Lookup[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadRejectionReasons();
      // Reset form when modal opens
      setRejectionReason('');
      setCustomReason('');
      setError(null);
    }
  }, [open]);

  const loadRejectionReasons = async () => {
    try {
      setLoadingReasons(true);
      const reasons = await lookupApi.getActiveLookupsByType('QuoteRejectionReason');
      setRejectionReasons(reasons);
    } catch (error) {
      console.error('Failed to load rejection reasons:', error);
      // Fallback to default reasons if API fails
      setRejectionReasons([
        { _id: '1', type: 'QuoteRejectionReason', value: 'Supplier rejected - new business', description: '', active: true, createdAt: '', updatedAt: '' },
        { _id: '2', type: 'QuoteRejectionReason', value: 'Supplier rejected - other reasons', description: '', active: true, createdAt: '', updatedAt: '' },
        { _id: '3', type: 'QuoteRejectionReason', value: 'Client declined offer', description: '', active: true, createdAt: '', updatedAt: '' },
        { _id: '4', type: 'QuoteRejectionReason', value: 'Better offer found elsewhere', description: '', active: true, createdAt: '', updatedAt: '' },
        { _id: '5', type: 'QuoteRejectionReason', value: 'Contract terms not acceptable', description: '', active: true, createdAt: '', updatedAt: '' },
        { _id: '6', type: 'QuoteRejectionReason', value: 'Other', description: '', active: true, createdAt: '', updatedAt: '' },
      ]);
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleSubmit = async () => {
    if (!quote) return;

    const finalReason = rejectionReason === 'Other' ? customReason : rejectionReason;
    
    if (!finalReason.trim()) {
      setError('Please select or enter a rejection reason');
      return;
    }

    setRejecting(true);
    setError(null);

    try {
      await onReject(quote._id, finalReason);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to reject quote');
    } finally {
      setRejecting(false);
    }
  };

  if (!quote) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(25, 25, 25, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Reject Quote
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Quote Ref: {quote.quoteRef}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Quote Summary */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                  Quote Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Client
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {quote.client?.companyName || `${quote.client?.firstName} ${quote.client?.lastName}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Service Type
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {quote.meterType === 'MPAN' ? 'Electricity' : 'Gas'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Current Status
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {quote.status}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Quote Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {new Date(quote.dateOfQuotation).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Rejection Reason Selection */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Rejection Reason
            </Typography>
            
            {loadingReasons ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: 'white' }}>
                  Loading rejection reasons...
                </Typography>
              </Box>
            ) : (
              <TextField
                select
                fullWidth
                label="Select Rejection Reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
                helperText="Choose the reason for rejecting this quote"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4fc3f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#4fc3f7',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.6)',
                  },
                }}
              >
                {rejectionReasons.map((reason) => (
                  <MenuItem key={reason._id} value={reason.value}>
                    {reason.value}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>

          {/* Custom Reason Input */}
          {rejectionReason === 'Other' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Custom Rejection Reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Please specify the reason for rejection..."
                required
                helperText="Provide a detailed explanation for the rejection"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4fc3f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#4fc3f7',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.6)',
                  },
                  '& input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              />
            </Grid>
          )}

          {/* Warning Message */}
          <Grid item xs={12}>
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Warning:</strong> Rejecting this quote will permanently change its status to "REJECTED" 
                and it cannot be undone. The client will be notified of the rejection.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={rejecting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={rejecting || loadingReasons}
          startIcon={rejecting ? <CircularProgress size={20} /> : null}
        >
          {rejecting ? 'Rejecting Quote...' : 'Reject Quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteRejectionModal;
